import React, { useState } from 'react'
import { X, Check, Users, Zap } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'

const ENQUIRE_EMAIL = 'alan@tkstrat.net'

const PLANS = [
  {
    key: 'professional',
    name: 'Professional',
    icon: Zap,
    monthlyPrice: 29,
    annualPrice: 290,
    monthlyPriceKey: 'professional_monthly',
    annualPriceKey: 'professional_annual',
    limit: 30,
    seats: 1,
    color: '#2b5ea7',
    features: [
      '30 analyses per month',
      'All analytical frameworks',
      'Disinformation detection',
      'Export to Word & PowerPoint',
      'Email delivery',
      '1 seat',
    ],
  },
  {
    key: 'team',
    name: 'Team',
    icon: Users,
    enquireOnly: true,
    limit: 100,
    seats: 5,
    color: '#1a4a8a',
    badge: 'Best Value',
    features: [
      '100 analyses per month',
      'All analytical frameworks',
      'Disinformation detection',
      'Export to Word & PowerPoint',
      'Email delivery',
      'Up to 5 seats',
    ],
  },
]

function formatDate(isoString) {
  return new Date(isoString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  })
}

function formatAmount(amount, currency) {
  const symbol = currency === 'gbp' ? '£' : currency.toUpperCase() + ' '
  return `${symbol}${amount % 1 === 0 ? amount : amount.toFixed(2)}`
}

export default function UpgradeModal({ onClose, subscriptionDetail, getToken }) {
  const [billing, setBilling] = useState('monthly')
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(null)
  const { getToken: clerkGetToken } = useAuth()

  const resolvedGetToken = getToken || clerkGetToken

  const tier = subscriptionDetail?.tier || 'free'
  const isPaid = tier !== 'free'

  const handleUpgrade = async (plan) => {
    if (plan.enquireOnly) {
      window.location.href = `mailto:${ENQUIRE_EMAIL}?subject=Professional Plan Enquiry`
      return
    }
    setError(null)
    const priceKey = billing === 'annual' ? plan.annualPriceKey : plan.monthlyPriceKey
    setLoading(plan.key)
    try {
      const token = await resolvedGetToken()
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ priceKey }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Checkout failed')
      window.location.href = data.url
    } catch (err) {
      setError(err.message)
      setLoading(null)
    }
  }

  const handleManageBilling = async (action) => {
    setError(null)
    setLoading(action)
    try {
      const token = await resolvedGetToken()
      const res = await fetch('/api/create-portal', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Portal failed')
      window.location.href = data.url
    } catch (err) {
      setError(err.message)
      setLoading(null)
    }
  }

  // Build current plan label for paid users
  const tierLabel = tier === 'professional' ? 'Professional' : tier === 'team' ? 'Team' : ''
  const billingLabel = subscriptionDetail?.billing_period === 'annual' ? 'Annual' : 'Monthly'
  const currentPlanName = isPaid ? `${tierLabel} ${billingLabel}` : 'Free'
  const amountLabel = subscriptionDetail?.amount
    ? `${formatAmount(subscriptionDetail.amount, subscriptionDetail.currency)} / ${subscriptionDetail.billing_period === 'annual' ? 'year' : 'month'}`
    : null
  const renewalLabel = subscriptionDetail?.current_period_end
    ? `${subscriptionDetail.cancel_at_period_end ? 'Cancels' : 'Renews'} ${formatDate(subscriptionDetail.current_period_end)}`
    : null
  const queriesRemaining = isPaid
    ? (subscriptionDetail.monthly_limit - subscriptionDetail.analyses_used)
    : null

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        maxWidth: '640px',
        width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid #f0ede8',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>
              {isPaid ? 'Your Subscription' : 'Upgrade Loyal Sidekick'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#666' }}>
              {isPaid
                ? 'Manage your plan, or upgrade to a higher tier.'
                : "You've reached your free plan limit. Upgrade to continue your analysis."}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '2px' }}>
            <X size={20} />
          </button>
        </div>

        {/* Current subscription info (paid users only) */}
        {isPaid && (
          <div style={{
            margin: '16px 24px 0',
            padding: '16px',
            background: '#f0f5ff',
            border: '1px solid #c5d9f7',
            borderRadius: '10px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#2b5ea7', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {currentPlanName}
                </div>
                {amountLabel && (
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#1a1a1a', lineHeight: 1 }}>
                    {amountLabel}
                  </div>
                )}
                {renewalLabel && (
                  <div style={{ fontSize: '12px', color: subscriptionDetail.cancel_at_period_end ? '#c5221f' : '#666', marginTop: '4px' }}>
                    {renewalLabel}
                  </div>
                )}
              </div>
              {queriesRemaining !== null && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '26px', fontWeight: '800', color: '#2b5ea7', lineHeight: 1 }}>
                    {queriesRemaining}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    of {subscriptionDetail.monthly_limit} queries<br />remaining this month
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleManageBilling('upgrade')}
                disabled={loading !== null}
                style={{
                  padding: '7px 14px', background: '#2b5ea7', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                }}
              >
                {loading === 'upgrade' ? 'Opening…' : 'Manage billing'}
              </button>
              <button
                onClick={() => handleManageBilling('cancel')}
                disabled={loading !== null}
                style={{
                  padding: '7px 14px', background: 'none', color: '#c5221f',
                  border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                }}
              >
                {loading === 'cancel' ? 'Opening…' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        )}

        {/* Billing toggle (only affects Team plan) */}
        <div style={{ padding: '16px 24px 0', display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', background: '#f5f4f1', borderRadius: '8px', padding: '3px' }}>
            {['monthly', 'annual'].map(b => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                style={{
                  padding: '6px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  background: billing === b ? '#fff' : 'transparent',
                  color: billing === b ? '#2b5ea7' : '#888',
                  boxShadow: billing === b ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {b === 'monthly' ? 'Monthly' : 'Annual'}
                {b === 'annual' && (
                  <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '700', background: '#e8f0fc', color: '#2b5ea7', padding: '1px 5px', borderRadius: '4px' }}>
                    Save 17%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '16px 24px' }}>
          {PLANS.map(plan => {
            const Icon = plan.icon
            const isCurrentPlan = tier === plan.key
            const price = plan.enquireOnly ? null : (billing === 'annual' ? plan.annualPrice : plan.monthlyPrice)
            return (
              <div key={plan.key} style={{
                border: `2px solid ${isCurrentPlan ? plan.color : '#e8e6e1'}`,
                borderRadius: '12px', padding: '16px', position: 'relative',
                background: isCurrentPlan ? '#f0f5ff' : '#fff',
                display: 'flex', flexDirection: 'column',
              }}>
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: '-1px', right: '12px',
                    background: plan.color, color: '#fff',
                    fontSize: '11px', fontWeight: '700', padding: '2px 8px',
                    borderRadius: '0 0 6px 6px',
                  }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Icon size={18} color={plan.color} />
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a1a' }}>{plan.name}</span>
                </div>

                {plan.enquireOnly ? (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>Please enquire for pricing</div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '26px', fontWeight: '800', color: plan.color }}>£{price}</span>
                    <span style={{ fontSize: '13px', color: '#888', marginLeft: '4px' }}>/{billing === 'annual' ? 'year' : 'month'}</span>
                  </div>
                )}

                <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none', flex: 1 }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#444', marginBottom: '4px' }}>
                      <Check size={12} color={plan.color} style={{ marginTop: '2px', flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: plan.color, padding: '8px', background: '#e8f0fc', borderRadius: '6px' }}>
                    Current plan
                  </div>
                ) : plan.enquireOnly ? (
                  <a
                    href={`mailto:${ENQUIRE_EMAIL}?subject=Professional Plan Enquiry`}
                    style={{
                      display: 'block', width: '100%', padding: '9px', textAlign: 'center',
                      background: 'transparent', color: plan.color,
                      border: `1px solid ${plan.color}`, borderRadius: '8px',
                      fontSize: '13px', fontWeight: '700', textDecoration: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    Enquire
                  </a>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading !== null}
                    style={{
                      width: '100%', padding: '9px',
                      background: plan.color, color: '#fff',
                      border: 'none', borderRadius: '8px',
                      fontSize: '13px', fontWeight: '700',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s',
                    }}
                  >
                    {loading === plan.key ? 'Redirecting…' : `Upgrade to ${plan.name}`}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {error && (
          <div style={{ margin: '0 24px', padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#dc2626' }}>
            {error}
          </div>
        )}

        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #f0ede8', marginTop: '8px' }}>
          <span style={{ fontSize: '12px', color: '#999' }}>Secured by Stripe · Cancel anytime</span>
        </div>
      </div>
    </div>
  )
}
